import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Music, Plus, Star } from "lucide-react";

import narratorService from "@/services/narratorService";
import authService from "@/services/authService";
import NarratorHeader from "@/components/narrator/NarratorHeader";
import DemoCard from "@/components/narrator/DemoCard";
import UploadDemoModal from "@/components/narrator/UploadDemoModal";

function DemoPageShell({ children }) {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#fffaf2] dark:bg-[#050504]" data-testid="narrator-demos">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_78%_6%,rgba(245,207,117,0.24),transparent_24%),radial-gradient(circle_at_20%_95%,rgba(201,137,5,0.12),transparent_26%),linear-gradient(180deg,#ffffff_0%,#fffaf2_42%,#fff1d8_100%)] dark:bg-[radial-gradient(circle_at_78%_10%,rgba(224,165,27,0.30),transparent_24%),linear-gradient(180deg,#050504_0%,#090805_48%,#030302_100%)]" />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 -z-10 h-[46vh] opacity-60 [background:repeating-radial-gradient(ellipse_at_center,rgba(224,165,27,0.20)_0_1px,transparent_1px_18px)] dark:opacity-35" />
      <NarratorHeader />
      {children}
    </div>
  );
}

function DemoStatCard({ icon: Icon, label, value }) {
  return (
    <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_16px_45px_rgba(94,64,18,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_16px_45px_rgba(0,0,0,0.36)]">
      <CardContent className="flex items-center gap-5 p-6">
        <div className="grid h-14 w-14 place-items-center rounded-full bg-[#fff2d8] text-[#c98905]">
          <Icon className="h-7 w-7" />
        </div>
        <div>
          <p className="text-sm text-slate-600 dark:text-[#d5c8ae]">{label}</p>
          <p className="mt-1 text-3xl font-bold leading-none text-black dark:text-[#f8efe0]">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function NarratorDemos() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [demos, setDemos] = useState([]);

  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDemo, setEditingDemo] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editLanguage, setEditLanguage] = useState("");
  const [editGenre, setEditGenre] = useState("");
  const [editTone, setEditTone] = useState("");

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const [profileData, demosData] = await Promise.all([
        narratorService.getProfile().catch(() => null),
        narratorService.getDemos().catch(() => []),
      ]);

      if (!profileData) {
        toast.error("Please complete your profile first");
        navigate("/narrator/onboarding");
        return;
      }

      setProfile(profileData);
      setDemos(Array.isArray(demosData) ? demosData : []);
    } catch (error) {
      console.error("Error loading demos:", error);
      if (error.response?.status === 401) {
        authService.clearTokens();
        navigate("/login");
      } else if (error.response?.status === 403) {
        toast.error("Narrator role required");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDemo = () => {
    setShowUploadModal(true);
  };

  const handleUploadComplete = async (result) => {
    // Refresh demo list after successful upload
    await loadData();
  };

  const handleSetFeatured = async (demoId) => {
    try {
      await narratorService.setFeaturedDemo(demoId);
      toast.success("Featured demo updated");
      await loadData();
    } catch (error) {
      console.error("Error setting featured:", error);
      toast.error("Failed to set featured demo");
    }
  };

  const handleSubmitDemo = async (demoId) => {
    try {
      await narratorService.submitDemo(demoId);
      toast.success("Demo submitted for review");
      await loadData();
    } catch (error) {
      console.error("Error submitting demo:", error);
      toast.error("Failed to submit demo");
    }
  };

  const handleEditDemo = (demo) => {
    setEditingDemo(demo);
    setEditTitle(demo.title);
    setEditLanguage(demo.language);
    setEditGenre(demo.genre_tag || "");
    setEditTone(demo.tone_tag || "");
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingDemo) return;

    try {
      await narratorService.updateDemo(editingDemo.id, {
        title: editTitle,
        language: editLanguage,
        genre_tag: editGenre || null,
        tone_tag: editTone || null,
      });
      toast.success("Demo updated");
      setShowEditModal(false);
      await loadData();
    } catch (error) {
      console.error("Error updating demo:", error);
      toast.error("Failed to update demo");
    }
  };

  const handleDeleteDemo = async (demo) => {
    const confirmed = window.confirm(
      `Delete "${demo.title}"? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await narratorService.deleteDemo(demo.id);
      toast.success("Demo deleted");
      await loadData();
    } catch (error) {
      console.error("Error deleting demo:", error);
      toast.error(error.response?.data?.detail || "Failed to delete demo");
    }
  };

  const handlePlayDemo = (demo) => {
    toast.info(`Playing: ${demo.title} (Audio playback stub)`);
  };

  const approvedDemos = demos.filter((d) => d.status === "APPROVED");
  const pendingDemos = demos.filter((d) => d.status === "SUBMITTED");
  const draftDemos = demos.filter((d) => d.status === "DRAFT");

  if (loading) {
    return (
      <DemoPageShell>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b77900] mx-auto"></div>
            <p className="mt-4 text-slate-600 dark:text-[#d5c8ae]">Loading demos...</p>
          </div>
        </div>
      </DemoPageShell>
    );
  }

  return (
    <DemoPageShell>
      <main className="relative z-10 mx-auto max-w-[1640px] px-5 py-8 md:px-8">

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1
              className="text-4xl font-bold mb-2 dark:text-[#f8efe0]"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Demo Portfolio
            </h1>
            <p className="text-slate-600 dark:text-[#d5c8ae]">
              Manage and showcase your voice demo clips
            </p>
          </div>

          <Button
            onClick={handleUploadDemo}
            className="bg-[#d99b13] px-7 text-black shadow-[0_12px_28px_rgba(185,119,0,0.22)] hover:bg-[#f0bd43]"
            data-testid="upload-new-demo-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Upload New Demo
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3">
          <DemoStatCard icon={Music} label="Approved" value={approvedDemos.length} />
          <DemoStatCard icon={Upload} label="Pending Review" value={pendingDemos.length} />
          <DemoStatCard icon={Star} label="Drafts" value={draftDemos.length} />
        </div>

        {/* Demo List */}
        <div className="space-y-6">
          {/* Approved */}
          {approvedDemos.length > 0 && (
            <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Badge className="bg-[#fff2d8] text-[#9a6500]">Approved</Badge>
                  <span className="text-slate-600 font-normal text-sm">
                    ({approvedDemos.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {approvedDemos.map((demo) => (
                  <DemoCard
                    key={demo.id}
                    demo={demo}
                    isFeatured={profile?.featured_demo_id === demo.id}
                    onPlay={handlePlayDemo}
                    onSetFeatured={handleSetFeatured}
                    onSubmit={handleSubmitDemo}
                    onEdit={handleEditDemo}
                    onDelete={handleDeleteDemo}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Pending */}
          {pendingDemos.length > 0 && (
            <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Badge className="bg-[#fff2d8] text-[#9a6500]">Pending Review</Badge>
                  <span className="text-slate-600 font-normal text-sm">
                    ({pendingDemos.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingDemos.map((demo) => (
                  <DemoCard
                    key={demo.id}
                    demo={demo}
                    isFeatured={false}
                    onPlay={handlePlayDemo}
                    onSetFeatured={handleSetFeatured}
                    onSubmit={handleSubmitDemo}
                    onEdit={handleEditDemo}
                    onDelete={handleDeleteDemo}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Drafts */}
          {draftDemos.length > 0 && (
            <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Badge className="bg-[#fff2d8] text-[#9a6500]">Drafts</Badge>
                  <span className="text-slate-600 font-normal text-sm">
                    ({draftDemos.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {draftDemos.map((demo) => (
                  <DemoCard
                    key={demo.id}
                    demo={demo}
                    isFeatured={false}
                    onPlay={handlePlayDemo}
                    onSetFeatured={handleSetFeatured}
                    onSubmit={handleSubmitDemo}
                    onEdit={handleEditDemo}
                    onDelete={handleDeleteDemo}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Empty state */}
          {demos.length === 0 && (
            <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_22px_70px_rgba(82,55,12,0.10)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_22px_70px_rgba(0,0,0,0.45)]">
              <CardContent className="py-20 text-center">
                <div className="mx-auto mb-5 grid h-32 w-32 place-items-center rounded-full bg-[#fff2d8] text-[#c98905]">
                  <Music className="h-20 w-20" />
                </div>
                <h3
                  className="mb-3 text-2xl font-semibold text-black dark:text-[#f8efe0]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  No demos yet
                </h3>
                <p className="mb-7 text-slate-600 dark:text-[#d5c8ae]">
                  Upload your first demo clip to showcase your voice
                </p>
                <Button
                  onClick={handleUploadDemo}
                  className="bg-[#d99b13] px-8 text-black shadow-[0_12px_28px_rgba(185,119,0,0.22)] hover:bg-[#f0bd43]"
                  data-testid="upload-first-demo-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Demo
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-white dark:border-[#4d3d16] dark:bg-[#0d0c09] dark:text-[#f8efe0]">
          <DialogHeader>
            <DialogTitle>Edit Demo Details</DialogTitle>
            <DialogDescription>
              Update the metadata for your demo clip
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="editTitle">Title</Label>
              <Input
                id="editTitle"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Demo title"
                data-testid="edit-demo-title"
              />
            </div>

            <div>
              <Label htmlFor="editLanguage">Language</Label>
              <Input
                id="editLanguage"
                value={editLanguage}
                onChange={(e) => setEditLanguage(e.target.value)}
                placeholder="e.g., English"
                data-testid="edit-demo-language"
              />
            </div>

            <div>
              <Label htmlFor="editGenre">Genre Tag</Label>
              <Input
                id="editGenre"
                value={editGenre}
                onChange={(e) => setEditGenre(e.target.value)}
                placeholder="e.g., Fiction, Non-fiction, Children"
                data-testid="edit-demo-genre"
              />
            </div>

            <div>
              <Label htmlFor="editTone">Tone Tag</Label>
              <Input
                id="editTone"
                value={editTone}
                onChange={(e) => setEditTone(e.target.value)}
                placeholder="e.g., Warm, Professional, Dramatic"
                data-testid="edit-demo-tone"
              />
            </div>

            <Button
              onClick={handleSaveEdit}
              className="w-full bg-[#d99b13] text-black hover:bg-[#f0bd43]"
              data-testid="save-demo-edit-btn"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Demo Modal */}
      <UploadDemoModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
      />
    </DemoPageShell>
  );
}
